import { computed } from 'vue';
import { UTCDateTime } from '@studyportals/mb-platform-date-time';
import {
	CampaignType, CustomProductValidator, OriginalCampaignDataDto, ValidationStateResponse
} from '@studyportals/campaign-management-api-interface';
import store from '@/store';

class DatePresenter {
	public shouldStartDateBeDisabled = computed((): boolean => {
		if (this.campaignType.value === CampaignType.CUSTOM_PRODUCTS) {
			return false;
		}

		if (!this.originalCampaignData.value?.baseCampaignData.endDate || !this.originalCampaignData.value.baseCampaignData.startDate) {
			return false;
		}

		if (this.isDateInThePast(this.originalCampaignData.value.baseCampaignData.endDate)) {
			return true;
		}

		if (this.isStartDateNotExecuted.value) {
			return false;
		}

		return this.isDateInThePastOrToday(this.originalCampaignData.value.baseCampaignData.startDate);
	});

	public shouldEndDateBeDisabled = computed((): boolean => {
		if (this.campaignType.value === CampaignType.CUSTOM_PRODUCTS) {
			return false;
		}

		if (!this.originalCampaignData.value?.baseCampaignData.endDate) {
			return false;
		}

		return this.isDateInThePast(this.originalCampaignData.value.baseCampaignData.endDate);
	});

	private campaignType = computed((): CampaignType | null => {
		return store.getters.campaignType();
	});

	private campaignReadinessInfo = computed((): ValidationStateResponse | null => {
		return store.getters.campaignReadinessInfo();
	});

	private originalCampaignData = computed((): OriginalCampaignDataDto | null => {
		return store.getters.originalCampaignData();
	});

	private isStartDateNotExecuted = computed((): boolean => {
		return this.campaignReadinessInfo.value !== null &&
			this.campaignReadinessInfo.value.campaignType !== CampaignType.CUSTOM_PRODUCTS &&
			!this.campaignReadinessInfo.value.startDateExecuted;
	});

	public formatDateString(dateString: string): string {
		const date = new Date(dateString.replace(' ', 'T'));

		if (isNaN(date.getTime())) {
			throw new Error(`DatePresenter.formatDateString: invalid date string: ${dateString}`);
		}

		const monthName = date.toLocaleString('en-GB', { month: 'long' });
		const day = date.getDate();
		const year = date.getFullYear();
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');

		return `${monthName} ${day}, ${year}<br>${hours}:${minutes}`;
	}

	public transformDateObjectToUTCDateString(value: Date, yearFirst = true): string {
		const date = UTCDateTime.fromDate(value);
		const isoString = date.toShortDateString();
		if (yearFirst) {
			return isoString;
		}

		return `${isoString.substring(8, 10)}-${isoString.substring(5, 7)}-${isoString.substring(0, 4)}`;
	}

	public transformUTCDateStringToDateObject(value: string): Date {
		if (!this.isUTCDateStringValid(value)) {
			throw new Error(`DatePresenter.transformUTCDateStringToDateObject: invalid date string: ${value}`);
		}

		const date = UTCDateTime.fromISOString(value);
		return date.externalizeDate();
	}

	public doAnyCustomProductDatesFallOutsideBounds(startDate: Date, endDate: Date): boolean {
		return store.getters.customProducts().some((customProduct) => {
			return !CustomProductValidator.validateDeliveryDate(startDate, endDate, customProduct.date);
		});
	}

	public doDateRangesOverlap(startA: Date, endA: Date, startB: Date, endB: Date): boolean {
		return startA <= endB && startB <= endA;
	}

	public isDateInThePast(date: Date): boolean {
		const inputDay = this.stripTimeFromDate(date);
		const todayDay = this.stripTimeFromDate(new Date());

		return inputDay < todayDay;
	}

	private isDateInThePastOrToday(date: Date): boolean {
		const inputDay = this.stripTimeFromDate(date);
		const todayDay = this.stripTimeFromDate(new Date());

		return inputDay <= todayDay;
	}

	private isUTCDateStringValid(value: string): boolean {
		if (value.length !== 10) {
			return false;
		}

		const numberIndexes = [0, 1, 2, 3, 5, 6, 8, 9];
		for (const index of numberIndexes) {
			if (isNaN(parseInt(value[index]))) {
				return false;
			}
		}

		const dashIndexes = [4, 7];
		for (const index of dashIndexes) {
			if (value[index] !== '-') {
				return false;
			}
		}

		return true;
	}

	private stripTimeFromDate(date: Date): Date {
		return new Date(date.getFullYear(), date.getMonth(), date.getDate());
	}
}

export default new DatePresenter();

// Exporting the class itself in addition to the default export is necessary to be able to cover all the functionality with unit tests.
export { DatePresenter };
